/**
 * packages/ui/declineCopy.mjs — RUN TA (steel). The words of a DECLINE, in one place, for every
 * surface that has to print one.
 *
 * ⚠️ THERE ARE TWO RUNS CALLED TA. The other one is in `apps/main` and its subject is the answer
 * REVEAL animation (`ClarkAnswerV2.jsx`, "THE PROSE DRAINS IN"). This module is the steel one's.
 * See `apps/steel/ops/run-ta/DECLARATION.md`.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🔴 WHY THIS IS A SHARED MODULE AND NOT A SECOND DECLINE RENDERER.
 *
 * RUN SC found *Calloway v. Meridian Freight Systems* — a case that does not exist — coming back
 * from the gate as `blocking: true, prose: ''` and PAINTING ZERO CHARACTERS OF DECLINE on Research.
 * What the page showed was a question, a "SEE ALSO" card and "AUTHORITIES 0". No sentence anywhere
 * said the product had refused. SC correctly declined to fix it by writing Research a decline
 * renderer of its own: Clark already had one, and two implementations of one rule is the bug this
 * product keeps paying for. `howWeKnow.mjs` exists for exactly that reason and this is its sibling.
 *
 * 🔴 NO JSX, ON PURPOSE — howWeKnow.mjs's rule, and for its reason: apps/main has no JSX renderer
 * in test, so a module both apps must agree about has to be executable by both apps' tests. Every
 * export here is plain data or a pure function over the envelope.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🔴🔴 THERE ARE TWO DECLINE POPULATIONS AND A RENDERER THAT COVERS ONE COVERS THE WRONG ONE.
 *
 * MEASURED, `apps/steel/ops/run-ta/step0-population.mjs`, over 50 archived envelopes (RA2's 36
 * probes, SC's 8 live turns, RUN PQ's 5 blocking envelopes + the generated capability fixture):
 *
 *   GATE decline    `blocking: true`. `prose` and `segments` are EMPTY on all 7 — 7/7, no
 *                   exceptions — because `toV2Envelope` empties them rather than marking them.
 *                   `grounding.declineKind` is `no_record_support` (6) or `capability` (1).
 *                   THIS IS THE ONE THAT PAINTS NOTHING, and it is **0 of RA2's 36 probes.**
 *   PROSE decline   `blocking: false`, `grounding.declineKind: null`, `grounding.verdict:
 *                   'grounded'` — and the composed prose IS the refusal. 8 of the 50, 523–2,139
 *                   characters each. SC's top-level `declined` is the only field that names it.
 *                   THIS IS THE ONE THAT ACTUALLY FIRES.
 *
 * ⭐ SO THE TWO GET DIFFERENT TREATMENT, AND THAT IS THE WHOLE DESIGN. A gate decline REPLACES the
 * answer, because there is no answer — every character on screen has to come from here. A prose
 * decline is ANNOTATED, because the refusal is already written and in the model's own words;
 * printing a second refusal over the top of it would be the surface talking over its own payload.
 *
 * 🔴 `declined` IS ABSENT ON 42 OF THE 50, because they predate the field. A stored row read back
 * through `/api/answer/stored/:id` today can carry no `declined` key at all, and this module does
 * NOT re-derive it — the predicate is a regex over model prose that lives in
 * `apps/steel/server/lib/answerDecline.js`, and copying it here would be the second implementation
 * this file exists to prevent. An absent field therefore costs a NOTE, never an answer: the prose
 * still paints itself, in full, exactly as it does today. ⚠️ That is a stated absence, not a
 * silent one — `declineOf` returns `null` and the surface renders an ordinary answer.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * 🔴 THE COPY MAY SAY WHAT WE COULD NOT DO. IT MAY NOT SAY WHAT THE LAW IS.
 *
 * `answerDecline.js`'s header states the rule this module is bound by: *"A consumer may WITHHOLD on
 * this. It may not ASSERT on it. Nothing may print a NEW claim about the world because this is
 * true."* Every string below is a statement about THIS PRODUCT — what it searched, what it could
 * not tie an answer to, what it could not verify. None of them is a statement about the law, and
 * none of them says an authority does not exist. ⭐ SC's distinction, matched deliberately: the
 * shipped card reads *"Named in the answer only to say it could not be verified"* — us, not the
 * world. `PROSE_DECLINE.body` says the same thing in a sentence, because on B19 the answer named
 * *Hendricks v. Vance Logistics* only to refuse it, and "we could not verify it" and "it does not
 * exist" are not the same claim and only one of them is ours to make.
 */

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
   🔴 THE TWO GATE KINDS NEVER COLLAPSE — ADAM'S RULING, LIFTED VERBATIM FROM `ClarkAnswerV2.jsx`.

   `groundingGate.js:189-201` decides: `capability` when composition produced NOTHING and nothing
   resolved; `no_record_support` everywhere else. Those are different facts about the world and a
   lawyer needs to know which one they are looking at:

     no_record_support  "the record does not support this"     — a statement about the CORPUS
     capability         "this is outside what Clark does"      — a statement about the PRODUCT

   Paraphrase loses the difference, so the two strings are written out in full and separately, and
   neither is built by interpolating into the other. `null` — a blocking envelope with no kind — is
   NOT quietly folded into `no_record_support`: it gets its own honest, narrower line, because
   claiming the corpus was searched and came up short is a claim, and an absent kind does not
   support it.

   ⭐ MEASURED BEFORE IT WAS LIFTED, AND THE TWO IMPLEMENTATIONS DID NOT AGREE — see the report's
   STEP 0. Research's `HonestDecline.jsx` keys its two strings on `cases.length > 0`, which is the
   SURFACE guessing the reason from what happens to be on screen rather than reading the one the
   gate emitted; and both of its strings promise *"The closest matching authorities are below"* on
   a population where **4 of 7 gate declines retrieved ZERO authorities**. That is not a shape to
   average with; it is a shape to replace. Clark's is keyed on `grounding.declineKind` — the
   server's own word — and is what is lifted here.
   ═════════════════════════════════════════════════════════════════════════════════════════════ */
export const DECLINE_COPY = {
  no_record_support: {
    head: "I can’t ground an answer to this in the record.",
    body: "I found authority on this question, but I couldn’t tie an answer to what those sources actually say — so I’m not going to write one. An answer I can’t point to a source for is the one thing this tool won’t give you.",
    bodyNothingFound: "Nothing in the corpus bears closely enough on this question to build an answer on. That is a limit of what I can see here, not a statement about what the law says.",
  },
  capability: {
    head: "That’s outside what Clark does.",
    body: "Clark answers questions about what the law says and shows you the authority behind each answer. It doesn’t draft documents, apply the law to a client’s facts, or advise on strategy.",
    bodyNothingFound: "Clark answers questions about what the law says and shows you the authority behind each answer. It doesn’t draft documents, apply the law to a client’s facts, or advise on strategy.",
  },
  unknown: {
    head: "I’m not going to answer this one.",
    body: "The check that runs before an answer is delivered didn’t pass, and I’d rather say so than hand you something I can’t stand behind.",
    bodyNothingFound: "The check that runs before an answer is delivered didn’t pass, and I’d rather say so than hand you something I can’t stand behind.",
  },
};

/* ═══════════════════════════════════════════════════════════════════════════════════════════════
   🔴 THE PROSE DECLINE'S NOTE — NEW COPY, AND THE ONE PLACE THIS RUN WROTE WORDS RATHER THAN
   MOVING THEM. Three constraints, all of them from `answerDecline.js`'s header:

     1. IT ANNOTATES, IT DOES NOT REPLACE. The refusal is already written, below this note, in the
        model's own words. A second refusal printed over it is the surface talking over its payload.
     2. IT IS A STATEMENT ABOUT THIS ANSWER, NOT ABOUT THE LAW. "in whole or in part" is load
        bearing: C30 refuses to reproduce one regulation verbatim and then answers for three
        paragraphs, and B23 declines a premise and then cites four real authorities. A note reading
        "this answer declines" flat would be false about both.
     3. IT REFUSES THE INFERENCE A READER WILL OTHERWISE MAKE. B19's answer names *Hendricks v.
        Vance Logistics* ONLY to say it cannot verify it. "We could not verify it" and "it does not
        exist" are different claims and only the first is ours. The second sentence says so.

   🔴 AND THE FAILURE DIRECTION IS DECLARED, BECAUSE THIS NOTE RIDES A CLASSIFIER. `declined` is a
   regex over model prose (0 false positives on the 30 non-declines RA2 archived, and it misses B23
   on purpose). A FALSE POSITIVE prints this note above a good answer — which says less than the
   answer does and asserts nothing about the world. A FALSE NEGATIVE leaves today's behaviour
   exactly as it is. Neither direction can produce a new claim, which is the property that makes it
   safe to render at all.
   ═════════════════════════════════════════════════════════════════════════════════════════════ */
export const PROSE_DECLINE = {
  head: "This answer declines, in whole or in part.",
  body: "What follows is the answer’s own account of what it could not do, shown as written. It is a statement about this answer, not a finding about the law: where it says an authority could not be verified, that is what we could not do — not a ruling that the authority does not exist.",
};

const plural = (n, one, many) => (n === 1 ? one : many);

/**
 * ⭐ THE COUNT, AND THE JURISDICTION ONLY WHEN THERE IS SOMETHING TRUE TO SAY ABOUT IT.
 *
 * A decline that does not say what it looked at is indistinguishable from a decline that did not
 * look, and the second is the one a reader will assume. The count is the gate's own denominator
 * (`grounding.searched.authoritiesRetrieved`), not a count of what happens to be on screen.
 *
 * 🔴🔴 `unscoped` IS A SEPARATE ARGUMENT FROM `where`, AND THAT IS THE WHOLE POINT OF THIS
 * SIGNATURE. `ClarkAnswerV2.jsx` resolved the jurisdiction through `scopeStampLabel`, which
 * returns `null` for TWO DIFFERENT FACTS — "the search was not narrowed" and "this id did not
 * resolve" — and then rendered `where || "all jurisdictions"`, which turns both of them into the
 * claim that the WHOLE CORPUS was searched. That is BUNDLE V7 · LANE 4 RULE 1 ("an unrecognised id
 * must not render as 'All jurisdictions'") arriving through a third door, and the comment sitting
 * directly above that line cites the rule as its own reason for existing.
 *
 * ⚠️ IT IS A LATENT HAZARD, NOT A LIVE DEFECT, AND THE MEASUREMENT SAYS SO: across all 50 archived
 * envelopes `grounding.searched.jurisdiction` takes four values — `all` (46), `CA` (2), `DE`, `WY`
 * — and every one of them resolves. **Population zero.** So this module does not change what any
 * surface prints today; it makes the caller SAY which null it means, so the ambiguity cannot cross
 * the seam into a second app. A caller that cannot resolve its id passes `unscoped: false` and gets
 * the count alone. No stamp is honest; a confident wrong one is not.
 *
 * @param {object}  arg
 * @param {number}  arg.retrieved     `grounding.searched.authoritiesRetrieved`
 * @param {string?} arg.where         a RESOLVED jurisdiction label, or null
 * @param {boolean} arg.unscoped      true ONLY when the search genuinely was not narrowed
 * @param {string}  arg.sourceSuffix  e.g. " (from your search)" — appended verbatim, or ""
 * @returns {string} the sentence, or '' when there is nothing true to say
 */
export function searchedClause({ retrieved = 0, where = null, unscoped = false, sourceSuffix = '' } = {}) {
  const n = Number(retrieved) || 0;
  /* ⚠️ THE SILENCE GUARD IS ON `where`, NOT ON `label`, AND A PARITY CHECK IS WHY. `ClarkAnswerV2`
     rendered this line on `(where || retrieved > 0)` — so an UNNARROWED search that retrieved
     NOTHING printed no line at all, and that is 3 of the 7 archived gate declines (RQ14, RQ16, D2).
     Guarding on the resolved label instead reproduces that exactly. Written out because the first
     version of this function guarded on `label`, which made those three declines start printing
     "Searched all jurisdictions · 0 authorities retrieved" — a new sentence on a live surface,
     arriving inside a change whose whole claim was that it moved strings without moving pixels. */
  const label = where || (unscoped ? 'all jurisdictions' : null);
  const count = n === 1 ? '1 authority retrieved' : `${n} authorities retrieved`;
  if (!where && n <= 0) return '';
  if (!label) return count;
  return `Searched ${label}${sourceSuffix || ''} · ${count}`;
}

/**
 * ⭐ THE COUNT OF AUTHORITIES THE ANSWER ITSELF DISOWNS — RUN SC's `badge.disclaimed`, said in
 * words. Rendered only when it is greater than zero, in the register SC's card already uses
 * (`Workspace.jsx:1578` — "Named in the answer only to say it could not be verified"), so the
 * group note and the card note cannot come to disagree about what a disclaimed row is.
 * @returns {string} the sentence, or '' when nothing is disclaimed
 */
export function disclaimedClause(badge) {
  const n = Number(badge?.disclaimed) || 0;
  if (n <= 0) return '';
  return `${n} ${plural(n, 'authority', 'authorities')} below ${plural(n, 'is', 'are')} named in the answer only to say ${plural(n, 'it', 'they')} could not be verified.`;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * THE ONE PLACE THAT DECIDES WHETHER AN ENVELOPE IS A DECLINE, AND WHICH KIND.
 *
 * 🔴 `blocking` IS TESTED FIRST AND ON EQUALITY. It is the field the server sets in one place and
 * says no client may overrule (`clarkAnswerV2.js:352-358`), and it is the only one of the two that
 * means "there is no answer here". `declined` is a superset — `declined = blocking ||
 * answerDeclines(prose)` — so testing it first would render a gate decline as a prose one and
 * annotate an empty string.
 *
 * ⚠️ AND NEITHER IS TESTED BY TRUTHINESS. `blocking === true`, `declined === true`. An absent field
 * on an old stored row is `undefined`, and `undefined` must reach the same branch as an envelope
 * that was classified and found not to be a decline — but it must reach it by NOT MATCHING, not by
 * being coerced. The distinction is invisible today and is what stops a future `declined: 'no'`
 * from painting a refusal.
 *
 * @param {object} answer the v2 envelope, verbatim off the wire
 * @returns {null | {mode:'gate', kind:string, head:string, body:string}
 *                 | {mode:'prose', head:string, body:string}}
 */
export function declineOf(answer) {
  if (!answer) return null;

  if (answer.blocking === true) {
    const raw = answer.grounding?.declineKind;
    const kind = raw === 'capability' ? 'capability'
      : raw === 'no_record_support' ? 'no_record_support' : 'unknown';
    const copy = DECLINE_COPY[kind];
    /* The gate's own denominator decides which body is true. `retrieved > 0` means authority was
       found and nothing could be tied to it; `0` means nothing was found at all. Two of the seven
       archived gate declines retrieved 0 and the difference is the whole sentence. */
    const retrieved = Number(answer.grounding?.searched?.authoritiesRetrieved ?? 0);
    return { mode: 'gate', kind, head: copy.head, body: retrieved > 0 ? copy.body : copy.bodyNothingFound };
  }

  if (answer.declined === true) {
    return { mode: 'prose', head: PROSE_DECLINE.head, body: PROSE_DECLINE.body };
  }

  return null;
}

/** The decline as one plain string. For tests, and for any surface that cannot render markup. */
export function declineText(answer) {
  const d = declineOf(answer);
  return d ? `${d.head} ${d.body}` : '';
}

export default { DECLINE_COPY, PROSE_DECLINE, declineOf, declineText, searchedClause, disclaimedClause };
